import { LoadClassification, MonitoredLoadValue, MonitoredOccupancyLoadValues, TimePeriod } from 'app/model/CORE';
import TimeUtils from './TimeUtils';
import TimePeriodUtils from './TimePeriodUtils';
import memoizeOne from 'memoize-one';
import { TvMeteoAlert } from 'app/model/SOL49';
import { MeteoLoadBar } from 'app/components/dashboard/network/flows/svg/MeteoLoadBar';

export default class MonitoredLoadUtils {
    /**
     * Extract the monitored period from monitored values
     * @param monitoredLoadValues
     */
    static getMonitoredPeriod(monitoredLoadValues: Array<MonitoredLoadValue>): TimePeriod {
        if (!monitoredLoadValues || monitoredLoadValues.length === 0) return undefined;

        const monitoredPeriod = Object.assign({}, monitoredLoadValues[0].timePeriod);
        for (let i = 1; i < monitoredLoadValues.length; i++) {
            TimeUtils.growToCover(monitoredPeriod, monitoredLoadValues[i].timePeriod);
        }
        return monitoredPeriod;
    }

    static getOccupancyMonitoredPeriod(occLoadValues: Array<MonitoredOccupancyLoadValues>): TimePeriod {
        if (!occLoadValues || occLoadValues.length === 0) return undefined;

        const timePeriod: TimePeriod = {};
        occLoadValues.forEach(occLoadValue => {
            const occTp = this.getMonitoredPeriod(occLoadValue.occupancyLoadValues);
            if (occTp) {
                TimePeriodUtils.growToCover(timePeriod, occTp);
            }
        });
        if (timePeriod.from && timePeriod.to) {
            return timePeriod;
        }
        return undefined;
    }

    /**
     * Extract the active periods from monitored values
     * * @param monitoredLoadValues
     */
    static getActivePeriods(monitoredLoadValues: Array<MonitoredLoadValue>): Array<TimePeriod> {
        if (!monitoredLoadValues || monitoredLoadValues.length === 0) return undefined;

        return monitoredLoadValues
            .filter(monitoredLoadValue => monitoredLoadValue.loadClassification !== LoadClassification.UNDEFINED)
            .map(monitoredLoadValue => monitoredLoadValue.timePeriod);
    }

    /**
     * Return the active period that interescts with the provided time period
     * @param activePeriods
     */
    static getActivePeriodForOccupancy(activePeriods: Array<TimePeriod>, timePeriod: TimePeriod) {
        if (activePeriods) {
            for (const activePeriod of activePeriods) {
                if (TimePeriodUtils.intersects(activePeriod, timePeriod)) {
                    return activePeriod;
                }
            }
        }
        return null;
    }

    /**
     * Calculate the inactive periods based in a given period, given a list of activePeriods
     * @param period
     * @param activePeriods
     */
    static getInactivePeriods(period: TimePeriod, activePeriods: Array<TimePeriod>): Array<TimePeriod> {
        let inactivePeriods = [period];
        activePeriods.forEach(activePeriod => {
            for (let i = 0; i < inactivePeriods.length; i++) {
                const inactivePeriod = inactivePeriods[i];
                if (TimePeriodUtils.intersects(inactivePeriod, activePeriod)) {
                    inactivePeriods.splice(i, 1);
                    inactivePeriods = inactivePeriods.concat(TimePeriodUtils.subtract(inactivePeriod, activePeriod));
                    break;
                }
            }
        });
        return inactivePeriods;
    }

    /**
     * Return a numeric value that represents load classification (used for comparison functions)
     * @param loadClassification
     */
    private static getLoadClassificationValue(loadClassification: LoadClassification): number {
        if (!loadClassification) return undefined;
        switch (loadClassification) {
            case LoadClassification.OVERLOAD:
                return 4;
            case LoadClassification.HIGH_THRESHOLD:
                return 3;
            case LoadClassification.LOW_THRESHOLD:
                return 2;
            case LoadClassification.NORMAL:
                return 1;
            case LoadClassification.UNDEFINED:
                return 0;
        }
    }

    static isOccupancyDisplayed(
        monitoredLoadValues: Array<MonitoredLoadValue>,
        settings: 'all' | 'sustain' | 'peak' | 'none',
    ): boolean {
        if (!settings || settings === 'all') return true;
        if (settings === 'none') return false;

        const highestLoadClassification = MonitoredLoadUtils.getHighestLoadClassification(monitoredLoadValues);
        switch (settings) {
            case 'sustain':
                return MonitoredLoadUtils.isLoadClassificationAtLeast(
                    highestLoadClassification,
                    LoadClassification.LOW_THRESHOLD,
                );
            case 'peak':
                return MonitoredLoadUtils.isLoadClassificationAtLeast(
                    highestLoadClassification,
                    LoadClassification.HIGH_THRESHOLD,
                );
        }
        return undefined;
    }

    static isEntryDisplayed(
        monitoredLoadValues: Array<MonitoredLoadValue>,
        settings: 'all' | 'low' | 'high' | 'overload',
    ): boolean {
        if (!settings || settings === 'all') return true;

        const highestLoadClassification = MonitoredLoadUtils.getHighestLoadClassification(monitoredLoadValues);

        switch (settings) {
            case 'overload':
                return MonitoredLoadUtils.isLoadClassificationAtLeast(
                    highestLoadClassification,
                    LoadClassification.OVERLOAD,
                );
            case 'high':
                return MonitoredLoadUtils.isLoadClassificationAtLeast(
                    highestLoadClassification,
                    LoadClassification.HIGH_THRESHOLD,
                );
            case 'low':
                return MonitoredLoadUtils.isLoadClassificationAtLeast(
                    highestLoadClassification,
                    LoadClassification.LOW_THRESHOLD,
                );
        }

        return undefined;
    }

    /**
     * Return true if the load classification passed as first parameter is greater or equal to the one passed as second parameter
     * @param loadClassification
     * @param other
     */
    static isLoadClassificationAtLeast(loadClassification: LoadClassification, reference: LoadClassification): boolean {
        return (
            MonitoredLoadUtils.getLoadClassificationValue(loadClassification) >=
            MonitoredLoadUtils.getLoadClassificationValue(reference)
        );
    }

    /**
     * Return the highest load classification encountered in an array of monitored load values
     * @param monitoredLoadValues
     */
    static getHighestLoadClassification(monitoredLoadValues: Array<MonitoredLoadValue>): LoadClassification {
        if (!monitoredLoadValues || monitoredLoadValues.length === 0) return null;
        return monitoredLoadValues
            .map(monitoredLoadValue => monitoredLoadValue.loadClassification)
            .reduce((highest, other) => {
                const highestValue = MonitoredLoadUtils.getLoadClassificationValue(highest);
                const otherValue = MonitoredLoadUtils.getLoadClassificationValue(other);
                return highestValue < otherValue ? other : highest;
            });
    }

    static getFillColor(loadClassification: LoadClassification) {
        switch (loadClassification) {
            case LoadClassification.NORMAL: // < 15mn
                return '#303030';
            case LoadClassification.LOW_THRESHOLD: // > 15mn
                return '#726648';
            case LoadClassification.HIGH_THRESHOLD: // > 30mn
                return '#CC9933';
            case LoadClassification.OVERLOAD: // > 45mn
                return '#FF0000';
            case LoadClassification.UNDEFINED:
            default:
                return 'rgba(0, 0, 0, 0.0)';
        }
    }

    static getTvMeteoAlertsLoadValues = memoizeOne((alerts: Array<TvMeteoAlert>, monitoredPeriod: TimePeriod) => {
        if (!monitoredPeriod || !alerts) return;
        return alerts
            .filter(alert => TimePeriodUtils.intersects(alert.timePeriod, monitoredPeriod))
            .map(
                tvMeteoAlert =>
                    ({
                        loadClassification: MeteoLoadBar.getLoadClassification(tvMeteoAlert.impactLevel),
                        timePeriod: TimePeriodUtils.intersection(tvMeteoAlert.timePeriod, monitoredPeriod),
                    }) as MonitoredLoadValue,
            );
    });
}
